import React, { useState } from 'react';
import { formatQuestionLabel } from '../utils/tableToOneLine';
import { deleteRegradeRequest } from '../utils/regradeBatchTitle';

function studentLabel(student) {
  if (!student || typeof student !== 'object') return '';
  const roll = String(student.roll || '').trim();
  const name = String(student.name || '').trim();
  if (roll && name) return `(${roll}) ${name}`;
  const full = String(student.studentName || '').trim();
  return full || name || roll || String(student.key || '').trim();
}

function questionText(request) {
  return (Array.isArray(request.questions) ? request.questions : [])
    .map((id) => formatQuestionLabel(id))
    .filter(Boolean)
    .join(', ');
}

export default function RegradeChip({ requests, folderPath, onOpen }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const list = (Array.isArray(requests) ? requests : [])
    .slice()
    .sort((a, b) => Number(a.slot) - Number(b.slot));
  if (!list.length) return null;
  const label = list.length > 1 ? `Re-Grade (${list.length})` : 'Re-Grade';

  const removeSlot = async (event, slot) => {
    event.stopPropagation();
    event.preventDefault();
    if (busy || !folderPath) return;
    const ok = window.confirm(`Remove Re-Grade ${slot} from this folder?`);
    if (!ok) return;
    setBusy(true);
    try {
      await deleteRegradeRequest(folderPath, slot);
    } catch (err) {
      window.alert(err.message || 'Could not remove this re-grade');
    } finally {
      setBusy(false);
    }
  };

  const removeAll = async (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (busy || !folderPath) return;
    const ok = window.confirm(list.length > 1
      ? `Remove all ${list.length} re-grade requests from this folder? The chip leaves the card.`
      : `Remove Re-Grade ${list[0].slot} from this folder? The chip leaves the card.`);
    if (!ok) return;
    setBusy(true);
    try {
      for (const request of list) {
        await deleteRegradeRequest(folderPath, request.slot);
      }
    } catch (err) {
      window.alert(err.message || 'Could not remove this re-grade');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{ position: 'relative', width: 'fit-content', display: 'inline-flex', alignItems: 'center', gap: 4 }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={onOpen}
        title="Open Check with the first re-grade request"
        aria-label={`${label}. ${list.length} request${list.length === 1 ? '' : 's'}`}
        style={{
          border: 'none',
          background: '#5b21b6',
          color: '#fff',
          borderRadius: 6,
          padding: '3px 8px',
          fontSize: '0.68rem',
          fontWeight: 700,
          cursor: 'pointer',
          whiteSpace: 'nowrap'
        }}
      >
        {label}
      </button>
      <button
        type="button"
        onClick={removeAll}
        disabled={busy || !folderPath}
        title="Remove this re-grade chip without opening Check"
        aria-label="Remove this re-grade chip"
        style={{
          border: '1px solid #c4b5fd',
          background: '#fff',
          color: '#5b21b6',
          borderRadius: 6,
          width: 22,
          height: 22,
          padding: 0,
          fontSize: 14,
          fontWeight: 700,
          lineHeight: 1,
          cursor: busy ? 'wait' : 'pointer'
        }}
      >
        ×
      </button>
      {open && (
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            position: 'absolute',
            left: 0,
            top: '100%',
            zIndex: 20,
            marginTop: 4,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: '8px 10px',
            boxShadow: '0 8px 20px rgba(15, 23, 42, 0.12)',
            minWidth: 240,
            maxWidth: 420,
            maxHeight: 320,
            overflowY: 'auto',
            color: '#0f172a',
            fontSize: '0.72rem',
            fontWeight: 600,
            lineHeight: 1.45
          }}
        >
          {list.map((request, index) => {
            const students = (Array.isArray(request.students) ? request.students : [])
              .map(studentLabel)
              .filter(Boolean);
            const questions = questionText(request);
            return (
              <div key={request.slot} style={{ padding: '6px 0', borderTop: index ? '1px solid #f1f5f9' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontWeight: 700, color: '#5b21b6' }}>Re-Grade {request.slot}</div>
                  <button
                    type="button"
                    onClick={(event) => removeSlot(event, request.slot)}
                    disabled={busy || !folderPath}
                    title={`Remove Re-Grade ${request.slot} from this chip`}
                    style={{
                      border: '1px solid #fecaca',
                      background: '#fff',
                      color: '#b91c1c',
                      borderRadius: 6,
                      padding: '1px 6px',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      cursor: busy ? 'wait' : 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Remove
                  </button>
                </div>
                <div style={{ color: '#334155' }}>{questions || 'No questions selected'}</div>
                {students.length === 0 ? (
                  <div style={{ color: '#64748b' }}>No students on this request</div>
                ) : students.map((labelText, studentIndex) => (
                  <div key={`${request.slot}-${studentIndex}`}>{labelText}</div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
